import { useTranslation } from 'react-i18next';
import { appVersionLabel, APP_COMMIT, APP_VERSION } from '../../lib/app-version.js';
import { useSubscriptionSnapshot } from '../../lib/billing/entitlements.js';
import { useSession } from '../../lib/session/session-context.js';
import { initialsOf } from './user-menu.js';

/**
 * The foot of the desktop sidebar: who is signed in, which plan the shop is on, and which build
 * of the app this is.
 *
 * A shop's counter tablet is shared, so the name is worth having in view rather than one click
 * away in the account menu. The plan says what the shop is paying for, with its status beside it
 * whenever that is anything but plain "active" — a trial, a missed payment — because that is the
 * one time an owner needs to notice it. The version is for support: "which version are you on"
 * gets read off the screen instead of guessed.
 *
 * Two shapes, because the sidebar has two widths. Beside the section column there is room for a
 * row; on the bare rail it stacks into the rail's width, keeping the first name and the plan.
 */
export function SidebarAccount({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const session = useSession();
  const snapshot = useSubscriptionSnapshot();

  const name = session?.userName ?? null;
  const planName = snapshot ? t(`plan.${snapshot.planKey}.name`) : null;
  const status =
    snapshot && snapshot.status !== 'active' ? t(`billing.status.${snapshot.status}`) : null;
  const version = appVersionLabel();

  if (compact) {
    return (
      <div
        title={[name, planName && t('nav.account.plan', { plan: planName }), version]
          .filter(Boolean)
          .join('\n')}
        className="flex flex-col items-center gap-1 border-t border-sidebar-border px-1 py-3 text-center"
      >
        {name ? (
          <>
            <span
              aria-hidden="true"
              className="flex size-8 items-center justify-center rounded-full bg-sidebar-panel text-xs font-semibold text-sidebar-accent-foreground"
            >
              {initialsOf(name)}
            </span>
            <span className="max-w-full truncate text-[0.6875rem] font-medium text-sidebar-accent-foreground">
              {name.trim().split(/\s+/)[0]}
            </span>
          </>
        ) : null}
        {planName ? (
          <span className="max-w-full truncate text-[0.625rem] text-sidebar-foreground">
            {planName}
          </span>
        ) : null}
        <span className="text-[0.625rem] leading-tight tabular-nums text-sidebar-foreground/60">
          v{APP_VERSION}
          {APP_COMMIT ? <span className="block">{APP_COMMIT}</span> : null}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 border-t border-sidebar-border px-3 py-3">
      {name ? (
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sidebar-panel text-sm font-semibold text-sidebar-accent-foreground"
        >
          {initialsOf(name)}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        {name ? (
          <p className="truncate text-sm font-semibold text-sidebar-accent-foreground">{name}</p>
        ) : null}
        {planName ? (
          <p className="truncate text-xs text-sidebar-foreground">
            {t('nav.account.plan', { plan: planName })}
            {status ? (
              <span className="ml-1.5 rounded bg-sidebar-accent/25 px-1.5 py-px text-[0.625rem] font-medium text-sidebar-accent-foreground">
                {status}
              </span>
            ) : null}
          </p>
        ) : null}
        <p className="mt-0.5 text-[0.6875rem] tabular-nums text-sidebar-foreground/60">
          {t('nav.account.version', { version })}
        </p>
      </div>
    </div>
  );
}
